/** ConnectorsManager: the /settings/connectors surface.
 *
 *  Two tabs, never mixed:
 *    Connected: configured instances grouped by category, each a
 *               per-instance card with a scope, health, counts, an
 *               inline editor, and an ops bar.
 *    Available: the full catalog grouped into seven user-facing
 *               categories; Configure requires an account identity.
 *
 *  Outlook Bridge is a first-class card under Work, health sourced
 *  from /api/work-connectors/health, same ops bar shape.
 *
 *  Self-contained on purpose: it owns its data loading (plain fetch +
 *  useState, the house convention) and reuses ScopePicker for the
 *  multi-tag scope model. Built mobile-first for 375px. */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";
import { GoogleScopePicker } from "../pages/GoogleScopePicker";
import { Clock } from "lucide-react";
import {
  Plug,
  RefreshCw,
  Pause,
  Play,
  Trash2,
  RotateCcw,
  Database,
  Settings,
  Mail,
  MessageSquare,
  MessageCircle,
  Linkedin,
  Calendar,
  FolderOpen,
  Image,
  Contact,
  Github,
  DollarSign,
  Search,
  Wrench,
  Loader2,
  Upload,
  ExternalLink,
  type LucideIcon,
} from "lucide-react";
import {
  api,
  type ConnectorInstance,
  type ConnectorsResponse,
  type ConnectorCatalogResponse,
  type ConnectorCatalogEntry,
  type WorkConnectorHealth,
} from "../lib/api";
import { getCsrfToken } from "../lib/auth";
import { ScopeChips, ScopePicker, type Scope } from "./ScopePicker";
import {
  loadScopeDefinitions,
  scopeChipMeta,
  type ScopeDefinitions,
  FALLBACK_SCOPE_DEFS,
} from "../lib/scope-meta";

const TARGET_CATEGORIES = [
  "Communication",
  "Productivity",
  "Social",
  "Financial",
  "Health",
  "Work",
  "Other",
] as const;
type TargetCategory = (typeof TARGET_CATEGORIES)[number];

// Catalog slug -> the OAuth provider key /api/connectors/google/start
// expects. Each Google service is its own connector instance keyed on
// (user_id, kind, account_email); the post-callback guard then routes
// to /onboarding/google-scope-picker for per-instance scope.
const GOOGLE_PROVIDER_BY_SLUG: Record<string, string> = {
  gmail: "gmail",
  google_calendar: "calendar",
  google_contacts: "google_contacts",
  google_drive: "google_drive",
  google_photos: "google_photos",
};

// Catalog DB slugs (connector_categories) remapped onto the seven
// user-facing buckets. Unknown slugs fall through to Other.
const SLUG_TO_CATEGORY: Record<string, TargetCategory> = {
  email_calendar: "Communication",
  messaging: "Communication",
  mobile: "Communication",
  files_notes: "Productivity",
  browsing: "Productivity",
  social: "Social",
  photos_media: "Social",
  streaming: "Social",
  finance: "Financial",
  shopping: "Financial",
  subscriptions: "Financial",
  health: "Health",
  career: "Work",
};

// Connector kinds whose only ingest path is a user-uploaded "Download
// Your Information" zip (the backend enforces the same set in
// EXPORT_IMPORT_SLUGS). Each gets an inline upload control in the
// expanded instance panel, since these connectors never poll.
const EXPORT_IMPORT_KINDS = new Set([
  "facebook",
  "instagram",
  "twitter_x",
  "threads",
  "linkedin",
]);
const EXPORT_HELP: Record<string, { url: string; label: string; minutes: string }> = {
  facebook:  { url: "https://www.facebook.com/dyi/",                 label: "facebook.com/dyi",       minutes: "Facebook can take a few hours" },
  instagram: { url: "https://www.instagram.com/download/request/",    label: "instagram.com/download", minutes: "Instagram can take a few hours" },
  twitter_x: { url: "https://x.com/settings/download_your_data",      label: "x.com/settings",         minutes: "X is usually minutes to a day" },
  threads:   { url: "https://www.instagram.com/download/request/",    label: "instagram.com/download", minutes: "Threads ships in the Instagram export" },
  linkedin:  { url: "https://www.linkedin.com/mypreferences/d/download-my-data", label: "linkedin.com/mypreferences/d/download-my-data", minutes: "LinkedIn is usually under an hour" },
};

// Connected-instance kinds carry no category, so map by kind.
const KIND_TO_CATEGORY: Record<string, TargetCategory> = {
  gmail: "Communication",
  calendar: "Communication",
  "outlook-bridge": "Communication",
  "mail-viewer": "Communication", // legacy: pre-rename rows
  whatsapp: "Communication",
  "android-messages": "Communication",
  outlook: "Work",
  teams: "Work",
  "opentext-email": "Work",
  github: "Productivity",
  obsidian: "Productivity",
  rss: "Productivity",
};

/** Brand icon by connector kind/slug. lucide only (no network
 *  images). Substring match so gmail / google_calendar / mail-viewer
 *  etc all resolve; generic Plug is the fallback. */
interface BrandMeta {
  Icon: LucideIcon;
  /** Tailwind classes for the icon tile (bg + icon color). */
  tile: string;
  /** 1-line value proposition. */
  value: string;
  /** Hint-only scope nudge (NOT a saved default). */
  scopeHint: string;
}

/** Per-kind brand metadata. lucide icons + brand-correct tailwind
 *  accents, no network fetches. Substring match so gmail /
 *  google_calendar / mail-viewer / outlook-bridge all resolve;
 *  generic Plug + slate is the fallback. */
// Static literal class strings (Tailwind JIT cannot see dynamically
// built class names, so each brand accent is a full literal).
const TILE = {
  red: "bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400",
  blue: "bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400",
  sky: "bg-sky-50 dark:bg-sky-950/30 text-sky-600 dark:text-sky-400",
  emerald: "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400",
  indigo: "bg-indigo-50 dark:bg-indigo-950/30 text-indigo-600 dark:text-indigo-400",
  green: "bg-green-50 dark:bg-green-950/30 text-green-600 dark:text-green-400",
  slate: "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300",
  amber: "bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400",
  violet: "bg-violet-50 dark:bg-violet-950/30 text-violet-600 dark:text-violet-400",
  pink: "bg-pink-50 dark:bg-pink-950/30 text-pink-600 dark:text-pink-400",
  cyan: "bg-cyan-50 dark:bg-cyan-950/30 text-cyan-600 dark:text-cyan-400",
  rose: "bg-rose-50 dark:bg-rose-950/30 text-rose-600 dark:text-rose-400",
  orange: "bg-orange-50 dark:bg-orange-950/30 text-orange-600 dark:text-orange-400",
  teal: "bg-teal-50 dark:bg-teal-950/30 text-teal-600 dark:text-teal-400",
} as const;

function brandMeta(kindOrSlug: string): BrandMeta {
  const k = (kindOrSlug || "").toLowerCase();
  const T = (c: keyof typeof TILE) => TILE[c];
  if (k.includes("gmail"))
    return { Icon: Mail, tile: T("red"), value: "Captures your inbox, sent, and drafts in real time", scopeHint: "Often personal" };
  if (k.includes("outlook") || k.includes("mail-viewer")) // includes outlook-bridge; mail-viewer kept for legacy rows
    return { Icon: Mail, tile: T("blue"), value: "Bridges your work Outlook mailbox from the bridge VM", scopeHint: "Often work" };
  if (k.includes("calendar") || k.includes("gcal"))
    return { Icon: Calendar, tile: T("sky"), value: "Weaves your events and meeting context into the timeline", scopeHint: "Often personal" };
  if (k.includes("linkedin"))
    return { Icon: Linkedin, tile: T("blue"), value: "Imports your professional network and activity export", scopeHint: "Often work" };
  if (k.includes("whatsapp"))
    return { Icon: MessageCircle, tile: T("emerald"), value: "Captures your WhatsApp conversations and commitments", scopeHint: "Often personal" };
  if (k.includes("teams"))
    return { Icon: MessageSquare, tile: T("indigo"), value: "Captures your Teams chats and channel mentions", scopeHint: "Often work" };
  if (k.includes("messages") || k.includes("sms") || k.includes("android"))
    return { Icon: MessageSquare, tile: T("green"), value: "Captures your SMS and messaging threads", scopeHint: "Often personal" };
  if (k.includes("github"))
    return { Icon: Github, tile: T("slate"), value: "Tracks your repos, issues, and PR activity", scopeHint: "Often work" };
  if (k.includes("drive") || k.includes("dropbox") || k.includes("files"))
    return { Icon: FolderOpen, tile: T("amber"), value: "Indexes your documents and shared files", scopeHint: "Often personal" };
  if (k.includes("obsidian") || k.includes("notion"))
    return { Icon: FolderOpen, tile: T("violet"), value: "Syncs your notes and wiki as first-class context", scopeHint: "Often personal" };
  if (k.includes("photo"))
    return { Icon: Image, tile: T("pink"), value: "Surfaces people and moments from your photos", scopeHint: "Often family" };
  if (k.includes("contact"))
    return { Icon: Contact, tile: T("cyan"), value: "Builds your people graph from your address book", scopeHint: "Often personal" };
  if (k.includes("facebook") || k.includes("threads") || k.includes("bluesky") || k.includes("mastodon"))
    return { Icon: MessageCircle, tile: T("blue"), value: "Imports your social activity from a data export", scopeHint: "Often personal" };
  if (k.includes("instagram"))
    return { Icon: Image, tile: T("rose"), value: "Imports your Instagram activity from a data export", scopeHint: "Often personal" };
  if (k.includes("twitter") || k === "x" || k.includes("twitter_x"))
    return { Icon: MessageSquare, tile: T("sky"), value: "Imports your X / Twitter activity from a data export", scopeHint: "Often personal" };
  if (k.includes("simplifi") || k.includes("finance") || k.includes("coinbase") || k.includes("cash") || k.includes("plaid") || k.includes("venmo"))
    return { Icon: DollarSign, tile: T("emerald"), value: "Tracks your accounts, spend, and subscriptions", scopeHint: "Often financial" };
  if (k.includes("opentext"))
    return { Icon: Mail, tile: T("blue"), value: "Bridges your OpenText work surface", scopeHint: "Often work" };
  if (k.includes("rss"))
    return { Icon: FolderOpen, tile: T("orange"), value: "Follows your subscribed feeds", scopeHint: "Often personal" };
  if (k.includes("health") || k.includes("fitbit") || k.includes("garmin"))
    return { Icon: DollarSign, tile: T("teal"), value: "Captures your health and activity signals", scopeHint: "Often private" };
  return { Icon: Plug, tile: T("slate"), value: "Connect this source to start capturing its signal", scopeHint: "Pick a scope" };
}

function BrandTile({ kind, size = "sm" }: { kind: string; size?: "sm" | "lg" }) {
  const { Icon, tile } = brandMeta(kind);
  const box = size === "lg" ? "p-2.5" : "p-2";
  const ic = size === "lg" ? "w-5 h-5" : "w-4 h-4";
  return (
    <div className={`mt-0.5 shrink-0 rounded-lg ${box} ${tile}`}>
      <Icon className={ic} />
    </div>
  );
}

function categoryForSlug(slug: string): TargetCategory {
  return SLUG_TO_CATEGORY[slug] ?? "Other";
}
function categoryForKind(kind: string): TargetCategory {
  return KIND_TO_CATEGORY[kind] ?? "Other";
}

type Health = "green" | "yellow" | "red";
function healthFromStatus(status: string | null, lastSyncAt: string | null): Health {
  const s = (status || "").toLowerCase();
  if (
    s.includes("invalid_grant") ||
    s.includes("expired") ||
    s.includes("revoked") ||
    s.includes("unauthorized") ||
    s.includes("401") ||
    s.startsWith("error")
  )
    return "red";
  if (!lastSyncAt) return "yellow";
  return "green";
}

function HealthDot({ health }: { health: Health }) {
  const tint =
    health === "green"
      ? "bg-emerald-500"
      : health === "yellow"
        ? "bg-amber-500"
        : "bg-rose-500";
  return (
    <span
      className={`inline-block w-2.5 h-2.5 rounded-full ${tint}`}
      title={`Health: ${health}`}
      aria-label={`Health: ${health}`}
    />
  );
}

function relTime(iso: string | null): string {
  if (!iso) return "never synced";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "unknown";
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const CADENCES = [
  { value: "15", label: "Every 15 min" },
  { value: "30", label: "Every 30 min" },
  { value: "60", label: "Hourly" },
  { value: "360", label: "Every 6 hours" },
  { value: "1440", label: "Daily" },
];

function OpsButton({
  onClick,
  disabled,
  children,
  tone = "slate",
}: {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  tone?: "slate" | "rose" | "sky";
}) {
  const tones: Record<string, string> = {
    slate:
      "bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-200",
    rose: "bg-rose-100 hover:bg-rose-200 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200",
    sky: "bg-sky-600 hover:bg-sky-700 text-white",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 rounded-lg text-xs font-semibold px-2.5 py-1.5 disabled:opacity-50 ${tones[tone]}`}
    >
      {children}
    </button>
  );
}

/** Shared centered modal shell: portal to body, scroll-lock,
 *  Escape + backdrop close. Mirrors ConfigureModal so every
 *  connector dialog behaves identically. */
function ModalShell({
  onClose,
  children,
}: {
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4 animate-fadeIn motion-reduce:animate-none"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-[min(28rem,90vw)] max-h-[90vh] overflow-y-auto rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-xl p-5 space-y-4 animate-popoverIn motion-reduce:animate-none"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

function DisconnectModal({
  c,
  onClose,
  onDone,
}: {
  c: ConnectorInstance;
  onClose: () => void;
  onDone: () => void;
}) {
  const name = c.privacy_label || c.kind;
  const account = c.privacy_label || c.account;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <ModalShell onClose={onClose}>
      <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">
        Disconnect {name}?
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400">
        This revokes Memso's access to {account}. The connector record
        stays so you can Reconnect later without losing your settings.
        Ingested data stays.
      </p>
      {err && (
        <p className="text-sm text-rose-600 dark:text-rose-400">{err}</p>
      )}
      <div className="flex items-center justify-end gap-3 pt-1">
        <button
          type="button"
          onClick={onClose}
          className="text-sm text-slate-500 hover:underline"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setErr(null);
            try {
              const r = await api.connectorDisconnect(c.id);
              if (!r.ok) throw new Error(r.error || "Disconnect failed.");
              onDone();
            } catch (e) {
              setErr(e instanceof Error ? e.message : "Disconnect failed.");
            } finally {
              setBusy(false);
            }
          }}
          className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50"
        >
          {busy ? "Disconnecting..." : "Disconnect"}
        </button>
      </div>
    </ModalShell>
  );
}

function DeleteModal({
  c,
  onClose,
  onDone,
}: {
  c: ConnectorInstance;
  onClose: () => void;
  onDone: () => void;
}) {
  const name = c.privacy_label || c.kind;
  const [wipe, setWipe] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [est, setEst] = useState<{
    packets: number;
    engagements: number;
    observations: number;
  } | null>(null);
  useEffect(() => {
    if (!wipe || est) return;
    api
      .connectorPurgeEstimate(c.id)
      .then((r) => {
        if (r.ok)
          setEst({
            packets: r.packets,
            engagements: r.engagements,
            observations: r.observations,
          });
      })
      .catch(() => undefined);
  }, [wipe, est, c.id]);
  return (
    <ModalShell onClose={onClose}>
      <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">
        Delete {name}?
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400">
        This removes the connector record and revokes Memso's access.
        Settings, labels, and scope assignment are wiped. To reconnect
        this account later, you'll go through Configure again.
      </p>
      <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
        <input
          type="checkbox"
          checked={wipe}
          onChange={(e) => setWipe(e.target.checked)}
          className="mt-0.5"
        />
        <span>Also delete data imported from this connector</span>
      </label>
      {wipe && (
        <div className="rounded-lg bg-rose-50 dark:bg-rose-950/30 border border-rose-200/70 dark:border-rose-800/50 px-3 py-2 text-[12px] text-rose-700 dark:text-rose-300">
          {est
            ? `Memso will remove ${est.packets} packets, ${est.engagements} engagements, and ${est.observations} observations derived solely from this source. Data shared with other sources stays. This cannot be undone except by re-importing.`
            : "Calculating what will be removed..."}
        </div>
      )}
      {err && (
        <p className="text-sm text-rose-600 dark:text-rose-400">{err}</p>
      )}
      <div className="flex items-center justify-end gap-3 pt-1">
        <button
          type="button"
          onClick={onClose}
          className="text-sm text-slate-500 hover:underline"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setErr(null);
            try {
              const r = await api.connectorDelete(c.id, wipe);
              if (!r.ok) throw new Error(r.error || "Delete failed.");
              onDone();
            } catch (e) {
              setErr(e instanceof Error ? e.message : "Delete failed.");
            } finally {
              setBusy(false);
            }
          }}
          className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50"
        >
          {busy
            ? "Deleting..."
            : wipe
              ? "Delete + Wipe Data"
              : "Delete"}
        </button>
      </div>
    </ModalShell>
  );
}

/** Inline upload control for data_export_import connectors. Meta / X
 *  killed third-party personal-data API access, so these connectors
 *  never poll. The user requests a DYI export, downloads the zip, and
 *  uploads it here. Re-importing a newer export is idempotent at the
 *  packet layer (dedupe by external_id), so this card stays useful
 *  forever rather than disappearing after one upload. */
function DataExportPanel({ kind, onChanged }: { kind: string; onChanged: () => void }) {
  const slug = kind;
  const help = EXPORT_HELP[slug];
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [hasFile, setHasFile] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [latest, setLatest] = useState<{
    id: string;
    status: string;
    items_total: number;
    items_new: number;
    items_known: number;
    uploaded_at: string;
    finished_at: string | null;
    error: string | null;
  } | null>(null);
  const refresh = () => {
    api
      .connectorExportStatus(slug)
      .then((d) => setLatest(d.latest))
      .catch(() => undefined);
  };
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);
  const inFlight = latest?.status === "queued" || latest?.status === "parsing";
  useEffect(() => {
    if (!inFlight) return;
    const t = setInterval(refresh, 1500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inFlight]);
  const doUpload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f || busy) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.uploadConnectorExport(slug, f);
      setMsg("Uploaded. Memso is parsing your export.");
      if (fileRef.current) fileRef.current.value = "";
      setHasFile(false);
      refresh();
      onChanged();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      setBusy(false);
    }
  };
  const status = (() => {
    if (!latest) return "No export imported yet.";
    if (latest.status === "queued") return "Queued, waiting to parse.";
    if (latest.status === "parsing") return "Parsing your export.";
    if (latest.status === "error") return `Failed: ${latest.error ?? "unknown error"}.`;
    if (latest.status === "done") {
      const when = new Date(latest.uploaded_at).toLocaleDateString();
      return `Last import: ${when} - ${latest.items_new.toLocaleString()} new, ${latest.items_known.toLocaleString()} already known.`;
    }
    return latest.status;
  })();
  return (
    <div className="rounded-lg border border-slate-200/70 dark:border-slate-700/50 p-3 space-y-2 bg-slate-50/60 dark:bg-slate-900/40">
      <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 dark:text-slate-200">
        <Upload className="w-3.5 h-3.5" /> Import an export
      </div>
      {help && (
        <p className="text-[11px] text-slate-500 dark:text-slate-400">
          Request your data at{" "}
          <a
            href={help.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-0.5 text-sky-600 hover:underline"
          >
            {help.label} <ExternalLink className="w-3 h-3" />
          </a>
          . {help.minutes}, then upload the zip here.
        </p>
      )}
      <p className="text-[11px] text-slate-500 dark:text-slate-400">{status}</p>
      <div className="flex items-center gap-2 flex-wrap">
        <input
          ref={fileRef}
          type="file"
          accept=".zip,application/zip,application/x-zip-compressed"
          onChange={() => setHasFile(!!fileRef.current?.files?.length)}
          className="block text-[11px] text-slate-600 dark:text-slate-300 file:mr-2 file:rounded-md file:border-0 file:bg-slate-200 dark:file:bg-slate-700 file:px-2 file:py-1 file:text-xs file:font-semibold file:text-slate-700 dark:file:text-slate-100"
        />
        <button
          type="button"
          onClick={doUpload}
          disabled={!hasFile || busy || inFlight}
          className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white text-xs font-semibold px-3 py-1.5"
        >
          {busy || inFlight ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Upload className="w-3.5 h-3.5" />
          )}
          {latest?.status === "done" ? "Import another" : "Upload export"}
        </button>
      </div>
      {msg && <p className="text-[11px] text-slate-500 dark:text-slate-400">{msg}</p>}
    </div>
  );
}

function InstanceCard({
  c,
  onChanged,
}: {
  c: ConnectorInstance;
  onChanged: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [discModal, setDiscModal] = useState<
    null | "disconnect" | "delete"
  >(null);
  const [label, setLabel] = useState(c.privacy_label ?? "");
  const [scope, setScope] = useState<Scope>((c.scope?.split(",")[0] as Scope) || "personal");
  const cfg = (() => {
    try {
      return c.config_json ? (JSON.parse(c.config_json) as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  })();
  const [cadence, setCadence] = useState(String((cfg.cadence_minutes as number) || 30));
  const [fixOpen, setFixOpen] = useState(false);
  const [fixData, setFixData] = useState<Awaited<
    ReturnType<typeof api.connectorHealth>
  > | null>(null);
  const [fixLoading, setFixLoading] = useState(false);
  const paused = c.enabled === 0;
  // enabled=0 because the user has not assigned a scope yet is a
  // distinct state from a deliberately paused connector.
  const pendingScope = paused && c.pending_scope_picker === 1;
  const health: Health = paused ? "yellow" : healthFromStatus(c.last_status, c.last_sync_at);
  const additionalTags = c.scope
    ? c.scope.split(",").map((s) => s.trim()).filter((s) => s && s !== scope)
    : [];

  const op = async (
    o: "pause" | "resume" | "refresh" | "backfill" | "disconnect",
  ) => {
    setBusy(o);
    try {
      await api.connectorOp(c.id, o);
      onChanged();
    } finally {
      setBusy(null);
    }
  };
  const reconnect = async () => {
    setBusy("reconnect");
    try {
      const r = await api.connectorReconnect(c.id);
      if (r.reconnectUrl) window.location.href = r.reconnectUrl;
    } finally {
      setBusy(null);
    }
  };
  const openFix = async () => {
    setFixOpen((v) => !v);
    if (!fixData) {
      setFixLoading(true);
      try {
        setFixData(await api.connectorHealth(c.id));
      } catch {
        setFixData(null);
      } finally {
        setFixLoading(false);
      }
    }
  };
  const save = async (nextScope: Scope) => {
    setBusy("save");
    try {
      await api.connectorUpdateSettings(c.id, {
        privacy_label: label.trim() || null,
        scope: nextScope,
        config: { ...cfg, cadence_minutes: Number(cadence) },
      });
      onChanged();
      setExpanded(false);
    } finally {
      setBusy(null);
    }
  };

  return (
    <li className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur">
      <div className="flex items-start gap-3 p-3 sm:p-4">
        <BrandTile kind={c.kind} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-slate-900 dark:text-slate-100 capitalize">
              {c.kind.replace(/[-_]/g, " ")}
            </span>
            <span className="text-slate-400">·</span>
            <span className="text-sm text-slate-600 dark:text-slate-300 truncate max-w-[180px] sm:max-w-[260px]">
              {c.privacy_label || c.account}
            </span>
            <HealthDot health={health} />
            {pendingScope ? (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                <Clock className="w-3 h-3" /> Pending scope
              </span>
            ) : (
              paused && (
                <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-700 dark:bg-slate-700/60 dark:text-slate-200">
                  Paused
                </span>
              )
            )}
          </div>
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            {c.primary_scope ? (
              (() => {
                const m = scopeChipMeta(c.primary_scope, c.sub_scope);
                return (
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${m.className}`}
                  >
                    {m.label}
                  </span>
                );
              })()
            ) : (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-300/70 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-700/50">
                <Clock className="w-3 h-3" /> Awaiting scope
              </span>
            )}
          </div>
          <div className="mt-1.5 flex items-center gap-3 flex-wrap text-[11px] text-slate-500 dark:text-slate-400">
            <span>{c.last_sync_at ? `Synced ${relTime(c.last_sync_at)}` : "Never synced"}</span>
            <span className="inline-flex items-center gap-1">
              <Database className="w-3 h-3" />
              {c.packet_count_30d ?? 0} items / 30d
            </span>
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-2">
          {health === "red" && (
            <button
              type="button"
              onClick={openFix}
              className="inline-flex items-center gap-1 rounded-lg border border-rose-400 bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-200 text-xs font-bold px-2 py-1"
            >
              <Wrench className="w-3.5 h-3.5" /> Fix
            </button>
          )}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            title={expanded ? "Close settings" : "Settings"}
            aria-label={expanded ? "Close settings" : "Settings"}
            aria-expanded={expanded}
            className={`inline-flex items-center justify-center rounded-lg p-1.5 transition-colors ${
              expanded
                ? "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300"
                : "text-sky-600 hover:bg-sky-50 hover:text-sky-700 dark:text-sky-400 dark:hover:bg-sky-900/30"
            }`}
          >
            <Settings
              className={`h-5 w-5 transition-transform duration-150 ${
                expanded ? "rotate-90" : ""
              }`}
            />
          </button>
        </div>
      </div>

      {fixOpen && (
        <div className="border-t border-rose-200/70 dark:border-rose-800/50 bg-rose-50/60 dark:bg-rose-950/20 p-3 sm:p-4 text-sm">
          {fixLoading ? (
            <span className="inline-flex items-center gap-2 text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Diagnosing...
            </span>
          ) : fixData ? (
            <div className="space-y-2">
              <p className="font-bold text-rose-700 dark:text-rose-200 flex items-center gap-1.5">
                <HealthDot health={fixData.health} /> Status:{" "}
                {fixData.last_status || fixData.health}
              </p>
              {fixData.error && (
                <p className="text-rose-700 dark:text-rose-300 break-words">
                  Error: {fixData.error}
                </p>
              )}
              <p className="text-slate-600 dark:text-slate-300">
                {fixData.suggested_action}
              </p>
              <p className="text-[11px] text-slate-500">
                Last successful sync:{" "}
                {fixData.last_sync_at
                  ? relTime(fixData.last_sync_at)
                  : "never"}
              </p>
              <div className="flex items-center gap-2 pt-1">
                <OpsButton onClick={reconnect} disabled={!!busy} tone="sky">
                  <RotateCcw className="w-3.5 h-3.5" /> Reconnect
                </OpsButton>
                <OpsButton
                  onClick={async () => {
                    await op("refresh");
                    setFixData(await api.connectorHealth(c.id));
                  }}
                  disabled={!!busy}
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Retry now
                </OpsButton>
              </div>
            </div>
          ) : (
            <span className="text-slate-500">
              Could not load diagnostics.
            </span>
          )}
        </div>
      )}

      {expanded && (
        <div className="border-t border-slate-200/70 dark:border-slate-700/50 p-3 sm:p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
              Account label
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={c.account}
                className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-sm font-normal text-slate-900 dark:text-slate-100"
              />
            </label>
            <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
              Sync cadence
              <select
                value={cadence}
                onChange={(e) => setCadence(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-sm font-normal text-slate-900 dark:text-slate-100"
              >
                {CADENCES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
              Primary scope
            </div>
            <ScopePicker
              subject={`this ${c.kind} account`}
              busy={busy === "save"}
              error={null}
              defaultScope={scope}
              primaryOnly
              submitLabel="Save changes"
              onSubmit={async (s) => {
                setScope(s);
                await save(s);
              }}
            />
            {additionalTags.length > 0 && (
              <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                Additional tags ({additionalTags.join(", ")}) are derived
                per-packet by classifiers and cannot be set here.
              </p>
            )}
          </div>

          {EXPORT_IMPORT_KINDS.has(c.kind) && (
            <DataExportPanel kind={c.kind} onChanged={onChanged} />
          )}

          <div className="flex items-center gap-2 flex-wrap pt-1">
            <OpsButton onClick={reconnect} disabled={!!busy}>
              <RotateCcw className="w-3.5 h-3.5" /> Reconnect
            </OpsButton>
            {paused ? (
              <OpsButton onClick={() => op("resume")} disabled={!!busy}>
                <Play className="w-3.5 h-3.5" /> Resume
              </OpsButton>
            ) : (
              <OpsButton onClick={() => op("pause")} disabled={!!busy}>
                <Pause className="w-3.5 h-3.5" /> Pause
              </OpsButton>
            )}
            <OpsButton onClick={() => op("refresh")} disabled={!!busy}>
              <RefreshCw className="w-3.5 h-3.5" /> Force refresh
            </OpsButton>
            <OpsButton onClick={() => op("backfill")} disabled={!!busy}>
              <Database className="w-3.5 h-3.5" /> Backfill
            </OpsButton>
            <OpsButton
              onClick={() => setDiscModal("disconnect")}
              disabled={!!busy}
              tone="rose"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Disconnect
              (reversible)
            </OpsButton>
            <OpsButton
              onClick={() => setDiscModal("delete")}
              disabled={!!busy}
              tone="rose"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete (permanent)
            </OpsButton>
          </div>
        </div>
      )}
      {discModal === "disconnect" && (
        <DisconnectModal
          c={c}
          onClose={() => setDiscModal(null)}
          onDone={() => {
            setDiscModal(null);
            onChanged();
          }}
        />
      )}
      {discModal === "delete" && (
        <DeleteModal
          c={c}
          onClose={() => setDiscModal(null)}
          onDone={() => {
            setDiscModal(null);
            onChanged();
          }}
        />
      )}
    </li>
  );
}

async function postCsrf(url: string, body?: unknown) {
  const token = getCsrfToken();
  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { "X-CSRF-Token": token } : {}),
    },
    credentials: "same-origin",
    body: body ? JSON.stringify(body) : undefined,
  });
}
async function putCsrf(url: string, body: unknown) {
  const token = getCsrfToken();
  return fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { "X-CSRF-Token": token } : {}),
    },
    credentials: "same-origin",
    body: JSON.stringify(body),
  });
}

function OutlookBridgeCard({
  health,
  bridge,
  onChanged,
}: {
  health: WorkConnectorHealth | undefined;
  bridge?: {
    url: string | null;
    version: string | null;
    last_seen_at: string | null;
    ok: boolean;
  } | null;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmDisc, setConfirmDisc] = useState(false);
  const enabled = !!health?.enabled;
  const dot: Health = health?.health ?? "yellow";
  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    try {
      await fn();
      onChanged();
    } finally {
      setBusy(null);
    }
  };
  return (
    <li className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur">
      <div className="flex items-start gap-3 p-3 sm:p-4">
        <div className="mt-0.5 shrink-0 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 p-2">
          <Plug className="w-4 h-4 text-indigo-600 dark:text-indigo-300" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-slate-900 dark:text-slate-100">
              Outlook Bridge
            </span>
            <HealthDot health={dot} />
            {!enabled && (
              <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                disabled
              </span>
            )}
          </div>
          <div className="mt-1.5">
            <ScopeChips scopes="work" />
          </div>
          <div className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">
            {health?.lastRunAt ? `Last run ${relTime(health.lastRunAt)}` : "No runs yet"}
            {health?.lastRunStatus ? ` · ${health.lastRunStatus}` : ""}
          </div>
          {bridge?.url && (
            <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400 break-all">
              <span className="font-mono">{bridge.url}</span>
              {bridge.version ? (
                <>
                  {" · build "}
                  <code className="rounded bg-slate-100 dark:bg-slate-800 px-1 py-0.5 font-mono">
                    {bridge.version}
                  </code>
                </>
              ) : null}
              {bridge.last_seen_at
                ? ` · seen ${relTime(bridge.last_seen_at)}`
                : bridge.ok
                  ? ""
                  : " · never reached"}
            </div>
          )}
        </div>
      </div>
      <div className="border-t border-slate-200/70 dark:border-slate-700/50 p-3 sm:p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <OpsButton
            onClick={() => (window.location.href = "/settings/connectors")}
            disabled={!!busy}
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reconnect
          </OpsButton>
          {enabled ? (
            <OpsButton
              onClick={() =>
                run("pause", () => putCsrf("/api/work-connectors/outlook", { enabled: false }))
              }
              disabled={!!busy}
            >
              <Pause className="w-3.5 h-3.5" /> Pause
            </OpsButton>
          ) : (
            <OpsButton
              onClick={() =>
                run("resume", () => putCsrf("/api/work-connectors/outlook", { enabled: true }))
              }
              disabled={!!busy}
            >
              <Play className="w-3.5 h-3.5" /> Resume
            </OpsButton>
          )}
          <OpsButton
            onClick={() => run("refresh", () => postCsrf("/api/work-connectors/outlook/run"))}
            disabled={!!busy}
          >
            <RefreshCw className="w-3.5 h-3.5" /> Force refresh
          </OpsButton>
          <OpsButton
            onClick={() =>
              run("backfill", () => postCsrf("/api/connectors/outlook-bridge/backfill/start"))
            }
            disabled={!!busy}
          >
            <Database className="w-3.5 h-3.5" /> Backfill
          </OpsButton>
          {confirmDisc ? (
            <span className="inline-flex items-center gap-1.5">
              <OpsButton
                onClick={() =>
                  run("disconnect", () =>
                    putCsrf("/api/work-connectors/outlook", { enabled: false }),
                  )
                }
                disabled={!!busy}
                tone="rose"
              >
                <Trash2 className="w-3.5 h-3.5" /> Confirm disconnect
              </OpsButton>
              <button
                type="button"
                onClick={() => setConfirmDisc(false)}
                className="text-xs text-slate-500 hover:underline"
              >
                Cancel
              </button>
            </span>
          ) : (
            <OpsButton onClick={() => setConfirmDisc(true)} disabled={!!busy} tone="rose">
              <Trash2 className="w-3.5 h-3.5" /> Disconnect
            </OpsButton>
          )}
        </div>
      </div>
    </li>
  );
}

function ConfigureModal({
  entry,
  onClose,
  onDone,
}: {
  entry: ConnectorCatalogEntry;
  onClose: () => void;
  onDone: () => void;
}) {
  const [label, setLabel] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [scope, setScope] = useState<Scope>((entry.privacy_scope as Scope) || "personal");
  const [sub, setSub] = useState<string>("");
  const [defs, setDefs] = useState<ScopeDefinitions>(FALLBACK_SCOPE_DEFS);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    void loadScopeDefinitions().then(setDefs);
  }, []);
  // Backend understands these as additive scope tags today; other
  // subs (general / social / medical / employer subs) ride along as
  // sub_scope for forward use once the backend reads it.
  const BACKEND_TAGS = new Set(["family", "financial", "business", "private"]);
  const dirty = label.trim().length > 0 || identifier.trim().length > 0;
  const requestClose = () => {
    if (
      dirty &&
      !window.confirm("Discard this connector setup? Your entries will be lost.")
    )
      return;
    onClose();
  };
  // Lock body scroll while the modal is open (restore on close).
  // Rendered through a portal to document.body so position:fixed is
  // viewport-relative, not trapped by the connectors list's
  // backdrop-blur containing block.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);
  // Escape-to-close, re-bound when dirtiness changes so the unsaved
  // guard sees current state instead of a stale closure.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") requestClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);
  const submit = async (nextScope: Scope) => {
    if (!label.trim() || !identifier.trim()) {
      setErr("An account label and identifier are both required.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.configureConnector(entry.slug, {
        account_label: label.trim(),
        account_identifier: identifier.trim(),
        scope: nextScope,
        sub_scope: sub || null,
        additional_tags: sub && BACKEND_TAGS.has(sub) ? [sub] : [],
      });
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Configure failed.");
    } finally {
      setBusy(false);
    }
  };
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4 animate-fadeIn motion-reduce:animate-none"
      onClick={requestClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-[min(28rem,90vw)] rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-xl p-5 space-y-4 max-h-[90vh] overflow-y-auto animate-popoverIn motion-reduce:animate-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">
            Configure {entry.display_name}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            An account identity is required so captures attach to the
            right source.
          </p>
        </div>
        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300">
          Account label
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Work inbox"
            className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-sm font-normal text-slate-900 dark:text-slate-100"
          />
        </label>
        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300">
          Account identifier
          <input
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder="e.g. you@example.com or handle"
            className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-sm font-normal text-slate-900 dark:text-slate-100"
          />
        </label>
        <div>
          <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
            Scope
          </div>
          <ScopePicker
            subject={entry.display_name}
            busy={busy}
            error={err}
            defaultScope={scope}
            primaryOnly
            submitLabel={busy ? "Saving…" : "Configure connector"}
            onChange={(s) => {
              setScope(s);
              setSub("");
            }}
            onSubmit={async (s) => {
              setScope(s);
              await submit(s);
            }}
          />
          {(scope === "personal" || scope === "work") && (
            <label className="mt-2 block text-xs font-semibold text-slate-600 dark:text-slate-300">
              Sub-category
              <select
                value={sub}
                onChange={(e) => setSub(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-sm font-normal text-slate-900 dark:text-slate-100"
              >
                <option value="">
                  {scope === "personal" ? "General" : "Default"}
                </option>
                {(scope === "personal" ? defs.personal : defs.work).map(
                  (x) => (
                    <option key={x} value={x}>
                      {x.charAt(0).toUpperCase() + x.slice(1)}
                    </option>
                  ),
                )}
              </select>
              <span className="mt-1 block text-[11px] font-normal text-slate-400 dark:text-slate-500">
                Optional. Narrows where this connector's captures land.
              </span>
            </label>
          )}
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={requestClose}
            className="text-sm text-slate-500 hover:underline"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Pre-OAuth Configure step for Google. Google grants every service
 *  (Gmail, Calendar, Drive, Photos, Contacts) in a single
 *  authorization, so this explains what is about to happen and
 *  collects an optional default label before the redirect. Per-
 *  service scope is set afterwards on the configure-google step. */
function GoogleConfigureModal({
  entry,
  provider,
  returnTo,
  onClose,
}: {
  entry: ConnectorCatalogEntry;
  provider: string;
  returnTo: string;
  onClose: () => void;
}) {
  const [label, setLabel] = useState("");
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);
  const start = () => {
    const q = new URLSearchParams({ provider, returnTo });
    if (label.trim()) q.set("privacy_label", label.trim().slice(0, 120));
    window.location.href = `/api/connectors/google/start?${q.toString()}`;
  };
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4 animate-fadeIn motion-reduce:animate-none"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-[min(28rem,90vw)] max-h-[90vh] overflow-y-auto rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-xl p-5 space-y-4 animate-popoverIn motion-reduce:animate-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">
            Configure {entry.display_name}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            You are about to authorize Google to share data with Memso.
            Google grants access to multiple services (Gmail, Calendar,
            Drive, Photos, Contacts) in one authorization. After you
            authorize, you come back here to set the scope for each
            service Google grants.
          </p>
        </div>
        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300">
          Account label
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Personal Gmail (optional)"
            className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-sm font-normal text-slate-900 dark:text-slate-100"
          />
          <span className="mt-1 block text-[11px] font-normal text-slate-400 dark:text-slate-500">
            Used as the default label for every granted service. You
            can override it per service on the next step.
          </span>
        </label>
        <div className="flex items-center justify-end gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-slate-500 hover:underline"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={start}
            className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2"
          >
            Continue with Google
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function ConnectorsManager() {
  const { subsection, handle } = useParams<{
    subsection?: string;
    handle?: string;
  }>();
  // Sub-page surface. Connectors reads as its own app: a real nav
  // (My Connectors / Browse / Health / Logs) with deep-linkable
  // children, not a chip toggle. Default lands on My Connectors.
  const connectorsBase = handle
    ? `/u/${handle}/settings/connectors`
    : "/settings/connectors";
  const sub = (subsection || "connected").toLowerCase();
  const [pending, setPending] = useState<{ id: string }[]>([]);
  const [googleConnect, setGoogleConnect] = useState<{
    entry: ConnectorCatalogEntry;
    provider: string;
  } | null>(null);
  const [data, setData] = useState<ConnectorsResponse | null>(null);
  const [catalog, setCatalog] = useState<ConnectorCatalogResponse | null>(null);
  const [workHealth, setWorkHealth] = useState<WorkConnectorHealth[]>([]);
  // Live bridge identity (URL the daemon probes + last-seen build.sha
  // from /healthz pings) so the Outlook Bridge card shows which host
  // is configured and which build answered, with no auto-detect.
  const [outlookBridge, setOutlookBridge] = useState<{
    url: string | null;
    version: string | null;
    last_seen_at: string | null;
    ok: boolean;
  } | null>(null);
  const [configuring, setConfiguring] = useState<ConnectorCatalogEntry | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [scopeFilters, setScopeFilters] = useState<Set<string>>(new Set());
  const SCOPE_FILTER_CHIPS = [
    "personal",
    "work",
    "family",
    "financial",
    "business",
    "private",
  ];
  const toggleScopeFilter = (s: string) =>
    setScopeFilters((prev) => {
      const n = new Set(prev);
      if (n.has(s)) n.delete(s);
      else n.add(s);
      return n;
    });
  const [cat, setCat] = useState<TargetCategory | "All">("All");
  const RECOMMENDED = new Set([
    "gmail",
    "google_calendar",
    "outlook_bridge",
    "linkedin",
  ]);

  const load = () => {
    api.connectors().then(setData).catch(() => undefined);
    api.connectorCatalog().then(setCatalog).catch(() => undefined);
    api
      .workConnectorsHealth()
      .then((r) => setWorkHealth(r.connectors))
      .catch(() => undefined);
    api
      .workConnectorsBridgeHealth()
      .then((r) => {
        const o = r.bridges.find((b) => b.kind === "outlook");
        setOutlookBridge(
          o
            ? {
                url: o.url ?? null,
                version: o.version ?? null,
                last_seen_at: o.last_seen_at ?? null,
                ok: o.ok,
              }
            : null,
        );
      })
      .catch(() => undefined);
    fetch("/api/connectors/pending-scope", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) =>
        setPending(Array.isArray(d?.pending) ? d.pending : []),
      )
      .catch(() => setPending([]));
  };
  useEffect(load, []);

  const runAll = async () => {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.runConnectors();
      const total = Object.values(r.summary).reduce((a, s) => a + s.new, 0);
      setMsg(`Synced. ${total} new captures across ${Object.keys(r.summary).length} connectors.`);
      load();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Sync failed.");
    } finally {
      setBusy(false);
    }
  };

  const connectedByCategory = useMemo(() => {
    const q = search.trim().toLowerCase();
    const out = new Map<TargetCategory, ConnectorInstance[]>();
    for (const c of data?.connectors ?? []) {
      if (
        q &&
        !`${c.privacy_label ?? ""} ${c.account} ${c.kind}`
          .toLowerCase()
          .includes(q)
      )
        continue;
      if (scopeFilters.size > 0) {
        const cs = (c.scope || "")
          .split(",")
          .map((s) => s.trim().toLowerCase());
        if (![...scopeFilters].some((f) => cs.includes(f))) continue;
      }
      const cat = categoryForKind(c.kind);
      const arr = out.get(cat) ?? [];
      if (!out.has(cat)) out.set(cat, arr);
      arr.push(c);
    }
    return out;
  }, [data, search, scopeFilters]);

  const outlookHealth = workHealth.find((w) => w.kind === "outlook");

  const availableByCategory = useMemo(() => {
    const q = search.trim().toLowerCase();
    const out = new Map<TargetCategory, ConnectorCatalogEntry[]>();
    for (const cat of catalog?.categories ?? []) {
      for (const e of cat.connectors) {
        if (e.connected) continue; // Connected tab owns configured kinds.
        if (
          q &&
          !`${e.display_name} ${e.slug} ${e.description ?? ""}`
            .toLowerCase()
            .includes(q)
        )
          continue;
        const t = categoryForSlug(e.category || cat.slug);
        const arr = out.get(t) ?? [];
        if (!out.has(t)) out.set(t, arr);
        arr.push(e);
      }
    }
    // Alphabetical within each category.
    for (const arr of out.values())
      arr.sort((a, b) => a.display_name.localeCompare(b.display_name));
    return out;
  }, [catalog, search]);

  // Stable per-category counts for the tab strip (NOT affected by the
  // search box or scope chips, so the tab numbers stay steady).
  const catCounts = useMemo(() => {
    const conn = new Map<TargetCategory, number>();
    const avail = new Map<TargetCategory, number>();
    for (const c of data?.connectors ?? []) {
      const k = categoryForKind(c.kind);
      conn.set(k, (conn.get(k) ?? 0) + 1);
    }
    if (outlookHealth) conn.set("Work", (conn.get("Work") ?? 0) + 1);
    for (const grp of catalog?.categories ?? [])
      for (const e of grp.connectors) {
        if (e.connected) continue;
        const k = categoryForSlug(e.category || grp.slug);
        avail.set(k, (avail.get(k) ?? 0) + 1);
      }
    const total = (m: Map<TargetCategory, number>) =>
      [...m.values()].reduce((a, b) => a + b, 0);
    return {
      conn,
      avail,
      connTotal: total(conn),
      availTotal: total(avail),
      forCat: (t: TargetCategory) =>
        (conn.get(t) ?? 0) + (avail.get(t) ?? 0),
      allTotal: total(conn) + total(avail),
    };
  }, [data, catalog, outlookHealth]);

  const catsToShow: TargetCategory[] =
    cat === "All" ? [...TARGET_CATEGORIES] : [cat];
  const unhealthy = useMemo(() => {
    const bad = (s: string | null | undefined) =>
      !!s && !/ok|success|connected|idle|green|done/i.test(s);
    const rows: { id: string; label: string; detail: string }[] = [];
    for (const c of data?.connectors ?? [])
      if (bad(c.last_status))
        rows.push({
          id: c.id,
          label: `${c.privacy_label || c.kind} · ${c.account}`,
          detail: c.last_status || "needs attention",
        });
    for (const w of workHealth)
      if (w.health !== "green")
        rows.push({
          id: `work:${w.kind}`,
          label: w.displayName,
          detail: `${w.health}${
            w.lastRunStatus ? ` · ${w.lastRunStatus}` : ""
          }`,
        });
    return rows;
  }, [data, workHealth]);

  const NAV: { id: string; label: string; count?: number }[] = [
    { id: "connected", label: "My Connectors", count: catCounts.connTotal },
    { id: "browse", label: "Browse", count: catCounts.availTotal },
    { id: "health", label: "Health", count: unhealthy.length },
    { id: "logs", label: "Logs" },
  ];
  const recommended = useMemo(() => {
    const out: ConnectorCatalogEntry[] = [];
    for (const grp of catalog?.categories ?? [])
      for (const e of grp.connectors)
        if (!e.connected && RECOMMENDED.has(e.slug)) out.push(e);
    return out.slice(0, 4);
  }, [catalog]);

  // Route the Connect click by how the connector actually
  // authenticates. OAuth connectors must NOT open the data-export
  // Configure modal (that POSTs /configure and 400s with "slug X is
  // not a data-export connector"). Google opens a pre-OAuth Configure
  // dialog first, then the OAuth dance, then the configure-google
  // step. Each Google service is its own provider; the slug maps to
  // the provider key the backend OAuth start understands.
  const onConnect = (e: ConnectorCatalogEntry) => {
    const gp = GOOGLE_PROVIDER_BY_SLUG[e.slug];
    if (gp) {
      setGoogleConnect({ entry: e, provider: gp });
      return;
    }
    if (e.auth_kind === "data_export_import") {
      setConfiguring(e);
      return;
    }
    // Dedicated pairing / bridge surfaces (WhatsApp, Android Messages,
    // LinkedIn, Outlook Bridge) carry a real route that is not the
    // connectors page itself. Anything else has no working Connect
    // path yet; say so instead of opening a modal that 400s.
    if (e.config_route && e.config_route !== "/settings/connectors") {
      window.location.href = e.config_route;
      return;
    }
    setMsg(`${e.display_name} is not yet available to connect here.`);
  };

  const AvailableCard = ({
    e,
    i,
  }: {
    e: ConnectorCatalogEntry;
    i: number;
  }) => {
    const meta = brandMeta(e.slug);
    return (
      <div
        style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}
        className="group glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-3.5 flex flex-col gap-2 transition-all duration-150 hover:-translate-y-0.5 hover:shadow-lg hover:border-slate-300 dark:hover:border-slate-600 animate-fadeIn motion-reduce:animate-none"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2.5 min-w-0">
            <BrandTile kind={e.slug} size="lg" />
            <div className="min-w-0">
              <div className="font-bold text-sm text-slate-900 dark:text-slate-100 truncate">
                {e.display_name}
              </div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2 mt-0.5">
                {e.description || meta.value}
              </div>
            </div>
          </div>
          <span className="shrink-0 text-[10px] uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500">
            {categoryForSlug(e.category || "")}
          </span>
        </div>
        {e.status === "deprecated" ? (
          <div className="mt-auto pt-1 space-y-1.5">
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 dark:bg-slate-700/60 dark:text-slate-300">
              Disabled
            </span>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              This connector is disabled because Google retired its photo
              read API. Use the Memso Android Companion to capture photos
              going forward.
            </p>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2 mt-auto pt-1">
            <span className="text-[10px] rounded-full px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
              {meta.scopeHint}
            </span>
            <button
              type="button"
              onClick={() => onConnect(e)}
              className="inline-flex items-center gap-1 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold px-3 py-1.5"
            >
              Connect
            </button>
          </div>
        )}
      </div>
    );
  };

  const SUBTITLE: Record<string, string> = {
    connected: "Everything wired up, with its scope, health, and controls.",
    browse: "The full catalog. Pick a source to start capturing its signal.",
    health: "Connectors that need attention right now.",
    logs: "Recent connector activity.",
    "configure-google":
      "Set the scope for each Google service you just authorized.",
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100">
            Connectors
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {SUBTITLE[sub] ?? SUBTITLE.connected}
          </p>
        </div>
        <button
          type="button"
          onClick={runAll}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-3 py-2 disabled:opacity-50"
        >
          {busy ? "Syncing…" : "Sync all"}
        </button>
      </div>

      {pending.length > 0 && sub !== "configure-google" && (
        <div className="flex items-center gap-3 rounded-xl border border-amber-300/70 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
          <span className="flex-1">
            You have {pending.length} Google service
            {pending.length === 1 ? "" : "s"} pending scope assignment.
            Nothing is captured until each has a scope.
          </span>
          <Link
            to={`${connectorsBase}/configure-google`}
            className="shrink-0 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-1.5"
          >
            Configure now
          </Link>
        </div>
      )}

      {/* Primary sub-page nav. A real nav bar, not chip soup. */}
      <nav className="flex items-end gap-1 border-b border-slate-200/70 dark:border-slate-700/50">
        {NAV.map((n) => {
          const active = sub === n.id;
          return (
            <Link
              key={n.id}
              to={`${connectorsBase}/${n.id}`}
              className={`px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${
                active
                  ? "border-sky-600 text-sky-700 dark:text-sky-300 font-bold"
                  : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 font-medium"
              }`}
            >
              {n.label}
              {typeof n.count === "number" && (
                <span className="ml-1.5 text-[11px] opacity-70">
                  ({n.count})
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {sub === "configure-google" && (
        <GoogleScopePicker
          embedded
          redirectTo={`${connectorsBase}/connected`}
        />
      )}

      {sub === "logs" && (
        <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-10 text-center text-sm text-slate-500 dark:text-slate-400">
          Connector activity logs are coming soon.
        </div>
      )}

      {sub === "health" && (
        <div className="space-y-2">
          {unhealthy.length === 0 ? (
            <p className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-10 text-center text-sm text-slate-500 dark:text-slate-400">
              Every connector is healthy. Nothing needs attention.
            </p>
          ) : (
            <ul className="space-y-2">
              {unhealthy.map((u) => (
                <li
                  key={u.id}
                  className="flex items-center gap-3 rounded-xl border border-rose-200/70 dark:border-rose-800/50 bg-rose-50/60 dark:bg-rose-950/20 px-4 py-3"
                >
                  <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0" />
                  <span className="flex-1 text-sm font-semibold text-slate-800 dark:text-slate-100">
                    {u.label}
                  </span>
                  <span className="text-xs text-rose-700 dark:text-rose-300">
                    {u.detail}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {sub === "browse" && (
        <div className="flex items-center gap-1 flex-wrap border-b border-slate-200/70 dark:border-slate-700/50">
          {(["All", ...TARGET_CATEGORIES] as const).map((tc) => {
            const n =
              tc === "All" ? catCounts.allTotal : catCounts.forCat(tc);
            const activeTab = cat === tc;
            return (
              <button
                key={tc}
                type="button"
                onClick={() => setCat(tc)}
                className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
                  activeTab
                    ? "border-sky-600 text-sky-700 dark:text-sky-300 font-bold"
                    : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 font-medium"
                }`}
              >
                {tc}{" "}
                <span className="text-[11px] opacity-70">({n})</span>
              </button>
            );
          })}
        </div>
      )}

      {(sub === "connected" || sub === "browse") && (
        <>
          {msg && (
            <div className="text-xs text-slate-600 dark:text-slate-300">
              {msg}
            </div>
          )}

          {/* Prominent search. */}
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-3 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search connectors by name, capability, or category..."
              className="w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white/70 dark:bg-slate-900/40 pl-10 pr-3 py-2.5 text-sm"
            />
          </div>

          {sub === "connected" && (
            <div className="flex flex-wrap gap-1.5">
              {SCOPE_FILTER_CHIPS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleScopeFilter(s)}
                  className={`rounded-full px-2.5 py-1 text-[11px] capitalize border ${
                    scopeFilters.has(s)
                      ? "bg-sky-600 text-white border-sky-600"
                      : "border-slate-300 dark:border-slate-600 text-slate-500"
                  }`}
                >
                  {s}
                </button>
              ))}
              {(scopeFilters.size > 0 || search) && (
                <button
                  type="button"
                  onClick={() => {
                    setScopeFilters(new Set());
                    setSearch("");
                  }}
                  className="text-[11px] text-slate-400 hover:underline px-1"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}

          {/* Recommended (Browse, All category, no query). */}
          {sub === "browse" &&
            cat === "All" &&
            !search.trim() &&
            recommended.length > 0 && (
              <section className="space-y-2">
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                    Recommended for you
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Most users start here. These capture the broadest
                    signal.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {recommended.map((e, i) => (
                    <AvailableCard key={e.slug} e={e} i={i} />
                  ))}
                </div>
              </section>
            )}

          {/* Per-category list for the active section. */}
          <div className="space-y-6">
            {catsToShow.map((tc) => {
              const conn =
                sub === "connected"
                  ? connectedByCategory.get(tc) ?? []
                  : [];
              const avail =
                sub === "browse"
                  ? availableByCategory.get(tc) ?? []
                  : [];
              const showOutlook =
                sub === "connected" && tc === "Work" && !!outlookHealth;
              const empty =
                conn.length === 0 && avail.length === 0 && !showOutlook;
              if (empty && cat === "All") return null;
              return (
                <section key={tc} className="space-y-2">
                  <h3 className="text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                    {tc}
                  </h3>
                  {empty ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-6 text-center">
                      {sub === "connected"
                        ? "Nothing connected here yet. Browse to add a source."
                        : "No matches. Clear the search or pick another category."}
                    </p>
                  ) : (
                    <>
                      {conn.length > 0 || showOutlook ? (
                        <ul className="space-y-2">
                          {conn.map((c) => (
                            <InstanceCard
                              key={c.id}
                              c={c}
                              onChanged={load}
                            />
                          ))}
                          {showOutlook && (
                            <OutlookBridgeCard
                              health={outlookHealth}
                              bridge={outlookBridge}
                              onChanged={load}
                            />
                          )}
                        </ul>
                      ) : null}
                      {avail.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                          {avail.map((e, i) => (
                            <AvailableCard key={e.slug} e={e} i={i} />
                          ))}
                        </div>
                      ) : null}
                    </>
                  )}
                </section>
              );
            })}
          </div>
        </>
      )}

      {configuring && (
        <ConfigureModal
          entry={configuring}
          onClose={() => setConfiguring(null)}
          onDone={() => {
            setConfiguring(null);
            setMsg(`Configured ${configuring.display_name}.`);
            load();
          }}
        />
      )}
      {googleConnect && (
        <GoogleConfigureModal
          entry={googleConnect.entry}
          provider={googleConnect.provider}
          returnTo={`${connectorsBase}/configure-google`}
          onClose={() => setGoogleConnect(null)}
        />
      )}
    </div>
  );
}
